/**
 * The applicant History tab (flag applicant_view_v2): one read-only timeline assembled from what
 * already exists, newest first.
 *
 *   application created            applications/{id}.createdAt
 *   stage moves                    applications/{id}.stageHistory (pipeline, incl. Not moving forward)
 *   Do not pursue / Not moving forward reason   applications/{id}.notMovingForward
 *   status (when no stage history explains it)  applications/{id}.status + lastActionAt
 *   interviews and orientation     scheduling bookings (schedulingApi forApplication / listOrgSessions)
 *   emails sent                    each booking's and pick-a-time link's emailLog
 *   pick-a-time links              scheduling invites (schedulingApi forApplication inviteHistory)
 *   document requests              documentRequestsApi listForOrgCandidate
 *   offer                          offers (newest for this application)
 *   meeting proposals              listMeetingProposals
 *
 * Every source is loaded separately; the tab says which ones failed (with Retry) instead of
 * pretending there is nothing there. Pure: no Firebase; times are epoch ms.
 */
import {
  NOT_MOVING_FORWARD,
  PIPELINE_STAGE_LABELS,
  isPipelineStage,
} from './pipelineStages';

export type HistoryKind =
  | 'created'
  | 'stage'
  | 'not_pursued'
  | 'status'
  | 'interview'
  | 'orientation'
  | 'email'
  | 'invite_link'
  | 'document_request'
  | 'offer'
  | 'meeting';

export type HistoryItem = {
  id: string;
  atMs: number;
  kind: HistoryKind;
  title: string;
  detail?: string;
  /** Who did it, when known (a uid; the page turns it into a name). */
  byUid?: string | null;
  /** For interviews / orientation / meetings: when it takes place. */
  meetingAtMs?: number | null;
};

/** Firestore Timestamp, {seconds}, {_seconds}, Date, ms or ISO string -> ms (0 when unknown). */
export function toMs(v: unknown): number {
  if (!v) return 0;
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string') return Date.parse(v) || 0;
  const o = v as { toMillis?: () => number; seconds?: number; _seconds?: number };
  if (typeof o.toMillis === 'function') {
    try {
      return o.toMillis();
    } catch {
      return 0;
    }
  }
  if (typeof o.seconds === 'number') return o.seconds * 1000;
  if (typeof o._seconds === 'number') return o._seconds * 1000;
  return 0;
}

export type HistoryEmailLogEntry = { to?: unknown; kind?: unknown; outcome?: unknown; atMs?: unknown };

export type HistoryBooking = {
  id: string;
  kind?: unknown; // 'interview' | 'onboarding'
  stage?: unknown;
  status?: unknown; // 'booked' | 'cancelled'
  startMs?: unknown;
  createdVia?: unknown;
  emailLog?: HistoryEmailLogEntry[] | null;
  cancelledBy?: unknown;
  cancelReason?: unknown;
  sessionTitle?: unknown;
  hostName?: unknown;
};

export type HistoryInvite = { inviteId: string; stage?: unknown; status?: unknown; createdAtMs?: unknown; emailLog?: HistoryEmailLogEntry[] | null };

export type HistoryDocumentRequest = {
  id: string;
  createdAt?: unknown;
  requestedByName?: unknown;
  status?: unknown;
  dueDate?: unknown;
  jobId?: unknown;
  items?: Array<{ label?: unknown; status?: unknown }> | null;
};

export type HistoryOffer = { id?: string; createdAt?: unknown; updatedAt?: unknown; status?: unknown } | null;

export type HistoryMeetingProposal = { id: string; proposedAtMs?: unknown; status?: unknown; createdAtMs?: unknown; respondedAtMs?: unknown };

export type HistoryApplication = {
  createdAt?: unknown;
  status?: unknown;
  stageHistory?: unknown;
  notMovingForward?: unknown;
  lastActionAt?: unknown;
  updatedAt?: unknown;
  lastActionBy?: unknown;
};

export type HistoryInputs = {
  application: HistoryApplication;
  bookings?: HistoryBooking[] | null;
  invites?: HistoryInvite[] | null;
  documentRequests?: HistoryDocumentRequest[] | null;
  offer?: HistoryOffer;
  meetingProposals?: HistoryMeetingProposal[] | null;
};

const INTERVIEW_STAGE_WORDS: Record<string, string> = {
  interview_initial: 'Initial interview',
  interview_follow_up: 'Follow-up interview',
  interview_hiring: 'Hiring interview',
  onboarding_initial: 'Orientation',
};

const EMAIL_KIND_WORDS: Record<string, string> = {
  booked: 'confirmation',
  rescheduled: 'new time',
  cancelled: 'cancellation',
  reminder: 'reminder',
  invite_link: 'pick-a-time link',
};

const EMAIL_OUTCOME_WORDS: Record<string, string> = {
  sent: 'sent',
  failed: 'failed to send',
  suppressed_test_address: 'not sent (test address)',
  skipped_preference: 'not sent (email turned off by the recipient)',
  skipped_no_address: 'not sent (no email address)',
  skipped_policy: 'not sent',
  skipped_no_provider: 'not sent (email is not set up)',
  held_quiet_hours: 'held for quiet hours',
  skipped_quiet_hours: 'not sent (quiet hours)',
};

const STATUS_WORDS: Record<string, string> = {
  new: 'New',
  reviewed: 'Reviewed',
  interview_scheduled: 'Interview scheduled',
  offered: 'Offer sent',
  rejected: 'Not moving forward',
  archived: 'Archived',
};

const OFFER_STATUS_WORDS: Record<string, string> = {
  sent: 'waiting for an answer',
  accepted: 'accepted',
  declined: 'declined',
  withdrawn: 'withdrawn',
};

const MEETING_STATUS_WORDS: Record<string, string> = {
  accepted: 'accepted',
  declined: 'declined',
  reschedule_requested: 'asked for a different time',
};

const s = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

export function emailOutcomeWords(outcome: unknown): string {
  const o = s(outcome);
  return EMAIL_OUTCOME_WORDS[o] ?? (o ? o.replace(/_/g, ' ') : 'unknown');
}

function emailItems(prefix: string, what: string, log: HistoryEmailLogEntry[] | null | undefined): HistoryItem[] {
  return (Array.isArray(log) ? log : [])
    .map((e, i): HistoryItem | null => {
      const atMs = toMs(e?.atMs);
      if (!atMs) return null;
      const kindWord = EMAIL_KIND_WORDS[s(e.kind)] ?? (s(e.kind).replace(/_/g, ' ') || 'update');
      const to = s(e.to) === 'host' ? 'you' : 'the candidate';
      return {
        id: `${prefix}-email-${i}`,
        atMs,
        kind: 'email',
        title: `Email to ${to}: ${what} ${kindWord}`,
        detail: emailOutcomeWords(e.outcome),
      };
    })
    .filter((x): x is HistoryItem => !!x);
}

function stageWords(stage: unknown): string {
  if (stage === NOT_MOVING_FORWARD) return 'Not moving forward';
  return isPipelineStage(stage) ? PIPELINE_STAGE_LABELS[stage] : s(stage).replace(/_/g, ' ') || 'a stage';
}

export function buildApplicantHistory(input: HistoryInputs): HistoryItem[] {
  const items: HistoryItem[] = [];
  const app = input.application || {};

  const createdMs = toMs(app.createdAt);
  if (createdMs) items.push({ id: 'created', atMs: createdMs, kind: 'created', title: 'Applied' });

  // Stage moves (Not moving forward entries included), in stored order.
  const history = Array.isArray(app.stageHistory) ? (app.stageHistory as Array<Record<string, unknown>>) : [];
  const nmf = app.notMovingForward && typeof app.notMovingForward === 'object' ? (app.notMovingForward as Record<string, unknown>) : null;
  const nmfMs = nmf ? toMs(nmf.at) : 0;
  const nmfReason = nmf ? s(nmf.reason) : '';
  let nmfCovered = false;
  history.forEach((h, i) => {
    if (!h || typeof h !== 'object') return;
    const atMs = toMs(h.at);
    if (!atMs) return;
    const by = typeof h.by === 'string' ? h.by : null;
    if (h.stage === NOT_MOVING_FORWARD) {
      const isCurrent = !!nmf && Math.abs(atMs - nmfMs) < 2000;
      if (isCurrent) nmfCovered = true;
      items.push({
        id: `stage-${i}`,
        atMs,
        kind: 'not_pursued',
        title: 'Marked not moving forward',
        detail: isCurrent && nmfReason ? `Reason: ${nmfReason}` : undefined,
        byUid: by,
      });
    } else {
      items.push({ id: `stage-${i}`, atMs, kind: 'stage', title: `Moved to ${stageWords(h.stage)}`, byUid: by });
    }
  });
  if (nmf && nmfMs && !nmfCovered) {
    items.push({
      id: 'not-pursued',
      atMs: nmfMs,
      kind: 'not_pursued',
      title: 'Marked not moving forward',
      detail: nmfReason ? `Reason: ${nmfReason}` : undefined,
      byUid: typeof nmf.by === 'string' ? nmf.by : null,
    });
  }

  // The current status, when nothing above explains it (status changes made outside the pipeline:
  // the inbox, the old dropdown, scheduling). Only the latest status is stored, so only it shows.
  const status = s(app.status);
  const lastMs = toMs(app.lastActionAt) || toMs(app.updatedAt);
  if (status && status !== 'new' && lastMs && history.length === 0 && !nmf) {
    items.push({
      id: 'status',
      atMs: lastMs,
      kind: 'status',
      title: `Status: ${STATUS_WORDS[status] ?? status.replace(/_/g, ' ')}`,
      detail: s(app.lastActionBy) === 'emt' ? 'Last change by the candidate' : undefined,
    });
  }

  for (const b of input.bookings ?? []) {
    if (!b || !b.id) continue;
    const isOrientation = s(b.kind) === 'onboarding';
    const what = isOrientation ? s(b.sessionTitle) || 'Orientation' : INTERVIEW_STAGE_WORDS[s(b.stage)] ?? 'Interview';
    const startMs = toMs(b.startMs);
    const emails = emailItems(`booking-${b.id}`, isOrientation ? 'orientation' : 'interview', b.emailLog);
    const firstEmailMs = emails.length ? Math.min(...emails.map((e) => e.atMs)) : 0;
    const bookedAt = firstEmailMs || startMs;
    if (bookedAt) {
      items.push({
        id: `booking-${b.id}`,
        atMs: bookedAt,
        kind: isOrientation ? 'orientation' : 'interview',
        title: `${what} booked${s(b.createdVia) === 'candidate_link' ? ' by the candidate' : ''}`,
        detail: s(b.hostName) ? `With ${s(b.hostName)}` : undefined,
        meetingAtMs: startMs || null,
      });
    }
    if (s(b.status) === 'cancelled') {
      const cancelEmail = emails.find((e) => /cancellation/.test(e.title));
      items.push({
        id: `booking-${b.id}-cancelled`,
        atMs: cancelEmail?.atMs || bookedAt || startMs,
        kind: isOrientation ? 'orientation' : 'interview',
        title: `${what} cancelled${s(b.cancelledBy) === 'candidate' ? ' by the candidate' : ''}`,
        detail: s(b.cancelReason) ? `Reason: ${s(b.cancelReason)}` : undefined,
        meetingAtMs: startMs || null,
      });
    }
    items.push(...emails);
  }

  for (const inv of input.invites ?? []) {
    if (!inv || !inv.inviteId) continue;
    const createdAtMs = toMs(inv.createdAtMs);
    const what = INTERVIEW_STAGE_WORDS[s(inv.stage)] ?? 'Interview';
    if (createdAtMs) {
      const st = s(inv.status);
      items.push({
        id: `invite-${inv.inviteId}`,
        atMs: createdAtMs,
        kind: 'invite_link',
        title: `Pick-a-time link sent: ${what}`,
        detail: st === 'used' ? 'They picked a time' : st === 'revoked' ? 'Replaced by a newer link' : st === 'open' ? 'Not used yet' : undefined,
      });
    }
    items.push(...emailItems(`invite-${inv.inviteId}`, 'interview', inv.emailLog));
  }

  for (const r of input.documentRequests ?? []) {
    if (!r || !r.id) continue;
    const atMs = toMs(r.createdAt);
    if (!atMs) continue;
    const labels = (Array.isArray(r.items) ? r.items : []).map((it) => s(it?.label)).filter(Boolean);
    const st = s(r.status);
    const parts = [
      labels.length ? labels.slice(0, 5).join(', ') + (labels.length > 5 ? ` and ${labels.length - 5} more` : '') : '',
      s(r.dueDate) ? `due ${s(r.dueDate)}` : '',
      st === 'complete' ? 'complete' : st === 'cancelled' ? 'cancelled' : st === 'open' ? 'open' : '',
    ].filter(Boolean);
    items.push({
      id: `docreq-${r.id}`,
      atMs,
      kind: 'document_request',
      title: `Documents requested${s(r.requestedByName) ? ` by ${s(r.requestedByName)}` : ''}`,
      detail: parts.join(' · ') || undefined,
    });
  }

  if (input.offer) {
    const atMs = toMs(input.offer.createdAt);
    if (atMs) {
      const st = s(input.offer.status);
      items.push({ id: `offer-${input.offer.id ?? 'latest'}`, atMs, kind: 'offer', title: 'Offer sent', detail: OFFER_STATUS_WORDS[st] ? `Offer ${OFFER_STATUS_WORDS[st]}` : undefined });
      const answeredMs = toMs(input.offer.updatedAt);
      if ((st === 'accepted' || st === 'declined' || st === 'withdrawn') && answeredMs && answeredMs > atMs) {
        items.push({ id: `offer-${input.offer.id ?? 'latest'}-${st}`, atMs: answeredMs, kind: 'offer', title: `Offer ${OFFER_STATUS_WORDS[st]}` });
      }
    }
  }

  for (const p of input.meetingProposals ?? []) {
    if (!p || !p.id) continue;
    const atMs = toMs(p.createdAtMs);
    if (!atMs) continue;
    const st = s(p.status);
    items.push({
      id: `meeting-${p.id}`,
      atMs,
      kind: 'meeting',
      title: 'Meeting time proposed',
      detail: st === 'proposed' ? 'Awaiting response' : MEETING_STATUS_WORDS[st] ? `Candidate ${MEETING_STATUS_WORDS[st]}` : undefined,
      meetingAtMs: toMs(p.proposedAtMs) || null,
    });
  }

  // Newest first; same moment keeps a stable order (created always last).
  return items
    .map((it, i) => ({ it, i }))
    .sort((a, b) => b.it.atMs - a.it.atMs || b.i - a.i)
    .map((x) => x.it);
}

/** Load state of one history source. */
export type HistorySourceState = 'loading' | 'ok' | 'error' | 'off';

export type HistorySourceKey = 'application' | 'scheduling' | 'orientation' | 'documents' | 'offer' | 'meetings';

export const HISTORY_SOURCE_LABELS: Record<HistorySourceKey, string> = {
  application: 'stage and status changes',
  scheduling: 'interviews and their emails',
  orientation: 'orientation sessions',
  documents: 'document requests',
  offer: 'offers',
  meetings: 'meeting proposals',
};

/**
 * What the tab may say about an empty or partial timeline. "empty" only when EVERY source loaded
 * (or is switched off) and nothing happened; a failed source is always named.
 */
export function historyLoadSummary(
  states: Partial<Record<HistorySourceKey, HistorySourceState>>,
  itemCount: number,
): { loading: boolean; failed: HistorySourceKey[]; empty: boolean } {
  const entries = Object.entries(states) as Array<[HistorySourceKey, HistorySourceState]>;
  const loading = entries.some(([, v]) => v === 'loading');
  const failed = entries.filter(([, v]) => v === 'error').map(([k]) => k);
  return { loading, failed, empty: !loading && failed.length === 0 && itemCount === 0 };
}
